import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Upload, Car, Bus, Truck, Ship, Plane, Train, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import api from '../../utils/api';

// Transport modes - determines what type of transport this vehicle is for
const TRANSPORT_MODES = [
  { value: 'Road', label: 'Road', icon: Car },
  { value: 'Ferry', label: 'Ferry', icon: Ship },
  { value: 'Boat', label: 'Boat', icon: Ship },
  { value: 'Flight', label: 'Flight', icon: Plane },
  { value: 'Train', label: 'Train', icon: Train },
];

// Categories per transport mode
const CATEGORIES_BY_MODE = {
  Road: [
    { value: 'Car', label: 'Car' },
    { value: 'SUV', label: 'SUV' },
    { value: 'MUV', label: 'MUV' },
    { value: 'Tempo', label: 'Tempo Traveller' },
    { value: 'Bus', label: 'Bus' },
    { value: 'Luxury', label: 'Luxury' },
  ],
  Ferry: [
    { value: 'Economy Ferry', label: 'Economy Ferry' },
    { value: 'Premium Ferry', label: 'Premium Ferry' },
    { value: 'Cruise', label: 'Cruise' },
  ],
  Boat: [
    { value: 'Speed Boat', label: 'Speed Boat' },
    { value: 'Houseboat', label: 'Houseboat' },
    { value: 'Glass Bottom', label: 'Glass Bottom Boat' },
  ],
  Flight: [
    { value: 'Domestic', label: 'Domestic' },
    { value: 'International', label: 'International' },
  ],
  Train: [
    { value: 'Chair Car', label: 'Chair Car' },
    { value: 'Sleeper', label: 'Sleeper' },
    { value: 'AC', label: 'AC Class' },
  ],
};

const VehicleMaster = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    vehicle_name: '',
    vehicle_code: '',
    total_seats: 4,
    seating_capacity: 4,
    transport_mode: 'Road',
    vehicle_category: 'Car',
    description: '',
    vehicle_image: '',
    sort_order: 0
  });
  
  // Filter for viewing vehicles by mode
  const [modeFilter, setModeFilter] = useState('all');

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vehicle-types?include_inactive=true');
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to load vehicle types');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_name: '',
      vehicle_code: '',
      total_seats: 4,
      seating_capacity: 4,
      transport_mode: 'Road',
      vehicle_category: 'Car',
      description: '',
      vehicle_image: '',
      sort_order: vehicles.length + 1
    });
    setEditingVehicle(null);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      vehicle_name: vehicle.vehicle_name || '',
      vehicle_code: vehicle.vehicle_code || '',
      total_seats: vehicle.total_seats || 4,
      seating_capacity: vehicle.seating_capacity || 4,
      transport_mode: vehicle.transport_mode || 'Road',
      vehicle_category: vehicle.vehicle_category || 'Car',
      description: vehicle.description || '',
      vehicle_image: vehicle.vehicle_image || '',
      sort_order: vehicle.sort_order || 0
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicle_name.trim()) {
      toast.error('Vehicle name is required');
      return;
    }

    try {
      if (editingVehicle) {
        await api.put(`/vehicle-types/${editingVehicle.vehicle_type_id}`, formData);
        toast.success('Vehicle type updated');
      } else {
        await api.post('/vehicle-types', formData);
        toast.success('Vehicle type created');
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle type');
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle type?')) return;
    
    try {
      await api.delete(`/vehicle-types/${vehicleId}`);
      toast.success('Vehicle type deleted');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle type');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // For new vehicles, convert to base64 directly
    if (!editingVehicle) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({ ...formData, vehicle_image: e.target.result });
      };
      reader.readAsDataURL(file);
      return;
    }

    // For existing vehicles, upload via API
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post(
        `/vehicle-types/${editingVehicle.vehicle_type_id}/image`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setFormData({ ...formData, vehicle_image: response.data.vehicle_image });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, vehicle_image: '' });
  };

  const getModeIcon = (mode) => {
    const m = TRANSPORT_MODES.find(t => t.value === mode);
    return m ? m.icon : Car;
  };

  const getCategoryLabel = (mode, category) => {
    const categories = CATEGORIES_BY_MODE[mode] || CATEGORIES_BY_MODE.Road;
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };
  
  // Handle transport mode change - reset category to first option of new mode
  const handleModeChange = (mode) => {
    const categories = CATEGORIES_BY_MODE[mode] || [];
    setFormData({ 
      ...formData, 
      transport_mode: mode,
      vehicle_category: categories[0]?.value || 'Car'
    });
  };
  
  // Filter vehicles by selected mode
  const filteredVehicles = modeFilter === 'all' 
    ? vehicles 
    : vehicles.filter(v => v.transport_mode === modeFilter);

  return (
    <Layout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vehicle Master</h1>
            <p className="text-gray-500 mt-1">Define vehicle types and transport operators (cars, ferries, boats, etc.)</p>
          </div>
          <Button onClick={openNewDialog} data-testid="add-vehicle-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle Type
          </Button>
        </div>
        
        {/* Mode Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant={modeFilter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setModeFilter('all')}
          >
            All ({vehicles.length})
          </Button>
          {TRANSPORT_MODES.map(mode => {
            const count = vehicles.filter(v => v.transport_mode === mode.value).length;
            const ModeIcon = mode.icon;
            return (
              <Button 
                key={mode.value}
                variant={modeFilter === mode.value ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setModeFilter(mode.value)}
                className="gap-1.5"
              >
                <ModeIcon className="w-3.5 h-3.5" />
                {mode.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Vehicle Table */}
        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-10">
            <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{modeFilter === 'all' ? 'No vehicle types found' : `No ${modeFilter} vehicles found`}</p>
            <Button onClick={openNewDialog} className="mt-4">Add Your First Vehicle</Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle/Operator</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Capacity</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVehicles.map((vehicle) => {
                  const ModeIcon = getModeIcon(vehicle.transport_mode);
                  const modeLabel = TRANSPORT_MODES.find(m => m.value === vehicle.transport_mode)?.label || vehicle.transport_mode;
                  const categoryLabel = getCategoryLabel(vehicle.transport_mode, vehicle.vehicle_category);
                  return (
                    <tr 
                      key={vehicle.vehicle_type_id} 
                      className={`hover:bg-gray-50 ${!vehicle.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                      data-testid={`vehicle-row-${vehicle.vehicle_type_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {vehicle.vehicle_image ? (
                            <img 
                              src={vehicle.vehicle_image} 
                              alt={vehicle.vehicle_name}
                              className="w-10 h-10 rounded object-cover border"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                              <ModeIcon className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{vehicle.vehicle_name}</p>
                            {vehicle.description && (
                              <p className="text-xs text-gray-500 truncate max-w-[200px]">{vehicle.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{vehicle.vehicle_code || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <ModeIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-700">{modeLabel}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{categoryLabel}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {vehicle.seating_capacity} pax
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {vehicle.is_active ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(vehicle)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(vehicle.vehicle_type_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingVehicle ? 'Edit Vehicle Type' : 'Add Vehicle Type'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Vehicle Image</Label>
                <div className="relative">
                  {formData.vehicle_image ? (
                    <div className="relative w-full h-40 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={formData.vehicle_image}
                        alt="Vehicle"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload image</span>
                      <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</span>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <span className="text-sm text-gray-600">Uploading...</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle Name *</Label>
                  <Input
                    value={formData.vehicle_name}
                    onChange={(e) => setFormData({ ...formData, vehicle_name: e.target.value })}
                    placeholder={formData.transport_mode === 'Ferry' ? 'e.g., Makruzz Gold' : 'e.g., Innova Crysta'}
                    required
                    data-testid="vehicle-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vehicle Code</Label>
                  <Input
                    value={formData.vehicle_code}
                    onChange={(e) => setFormData({ ...formData, vehicle_code: e.target.value.toUpperCase() })}
                    placeholder={formData.transport_mode === 'Ferry' ? 'e.g., MKG' : 'e.g., INC'}
                    maxLength={10}
                    data-testid="vehicle-code-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Transport Mode *</Label>
                  <Select
                    value={formData.transport_mode}
                    onValueChange={handleModeChange}
                  >
                    <SelectTrigger data-testid="transport-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSPORT_MODES.map((mode) => {
                        const ModeIcon = mode.icon;
                        return (
                          <SelectItem key={mode.value} value={mode.value}>
                            <div className="flex items-center gap-2">
                              <ModeIcon className="w-4 h-4" />
                              {mode.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={formData.vehicle_category}
                    onValueChange={(v) => setFormData({ ...formData, vehicle_category: v })}
                  >
                    <SelectTrigger data-testid="vehicle-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(CATEGORIES_BY_MODE[formData.transport_mode] || []).map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Seats *</Label>
                  <Input
                    type="number"
                    value={formData.total_seats}
                    onChange={(e) => setFormData({ ...formData, total_seats: parseInt(e.target.value) || 0 })}
                    min="1"
                    required
                    data-testid="total-seats-input"
                  />
                  <p className="text-xs text-gray-500">
                    {formData.transport_mode === 'Ferry' ? 'Total passenger capacity' : 'Actual seats in the vehicle'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Seating Capacity *</Label>
                  <Input
                    type="number"
                    value={formData.seating_capacity}
                    onChange={(e) => setFormData({ ...formData, seating_capacity: parseInt(e.target.value) || 0 })}
                    min="1"
                    required
                    data-testid="seating-capacity-input"
                  />
                  <p className="text-xs text-gray-500">
                    {formData.transport_mode === 'Ferry' ? 'Usable capacity per booking' : 'Capacity after luggage space'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  data-testid="sort-order-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description or notes"
                  rows={2}
                  data-testid="vehicle-description-input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" data-testid="save-vehicle-btn">
                  {editingVehicle ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default VehicleMaster;
